import type { FragmentData } from '@ts-gql/tag';
import { gql } from '@ts-gql/tag';

// Earnings queries
export const earningsItemDetailFragment = gql`
  fragment PayItems_EarningItemDetail on EarningItemDetail {
    id
    displayName
    description
    taxApplicableName
    multiplier
    type
    stp2Type
    status
    payRateBasis
    includeInSuperGuarantee
    includeInTimeAccruals
    includeAsTaxFreePayItem
    liabilityAccount {
      accountName
      id
    }
    expenseAccount {
      id
      accountName
    }
    terminationType
    typeName
    superItems {
      id
    }
    isQE
  }
` as import('../../../../__generated__/ts-gql/PayItems_EarningItemDetail').type;

export const EarningsList_EarningsQuery = gql`
  query earningsItems_listQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      earningItems {
        id
        ...PayItems_EarningItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/earningsItems_listQuery').type;

export const PayItem_DeleteEarningItemMutation = gql`
  mutation deleteEarningPayItem($bookId: ID!, $id: ID!) {
    deleteEarningItem(bookId: $bookId, id: $id) {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/deleteEarningPayItem').type;

export const PayItem_CreateEarningItemMutation = gql`
  mutation createEarningPayItem($input: CreateEarningItemWithSuperItemsInput!) {
    createEarningItemWithSuperItems(input: $input) {
      id
      ...PayItems_EarningItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/createEarningPayItem').type;

export const PayItem_UpdateEarningItemMutation = gql`
  mutation updateEarningPayItem($input: UpdateEarningItemWithSuperItemsInput!) {
    updateEarningItemWithSuperItems(input: $input) {
      id
      ...PayItems_EarningItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/updateEarningPayItem').type;

const PayItem_AccountEdgeFragment = gql`
  fragment PayItems_AccountEdge on LedgerAccount {
    id
    fullName
    accountType
    accountCode
  }
` as import('../../../../__generated__/ts-gql/PayItems_AccountEdge').type;

export type PayItemAccountEdge = FragmentData<
  typeof PayItem_AccountEdgeFragment
>;

export const PayItem_AccountQuery = gql`
  query payItem_AccountQuery(
    $bookId: ID!
    $accountTypes: [LedgerAccountTypeEnum!]
    $pageLimit: OneToFiveHundredInt!
    $pageNumber: Int!
    $sortField: String!
    $sortOrder: SortOrderEnum!
  ) {
    book(bookId: $bookId) {
      id
      ledgerAccountsConnection(
        input: {
          pageInputWithFiveHundredLimit: {
            limit: $pageLimit
            number: $pageNumber
            sort: { field: $sortField, order: $sortOrder }
          }
          where: { accountTypes: $accountTypes }
        }
      ) {
        pageInfo {
          hasNextPage
          total
        }
        edges {
          ...PayItems_AccountEdge
        }
      }
    }
  }
` as import('../../../../__generated__/ts-gql/payItem_AccountQuery').type;

// Leave queries

export const leaveItemDetailFragment = gql`
  fragment PayItems_LeaveItemDetail on LeaveItemDetail {
    id
    displayName
    status
    leaveItemType
    leaveItemSubType
    annualEntitlement
    accrualPeriod
    expenseAccount {
      id
      accountName
    }
    leaveLoadingPercentage
    description
    includeLeaveLoadingInSuperCalculation
    isInTimeWorkedAccrual
    isPayOnTermination
    isResetEveryYear
    isShownLeaveBalanceOnPaySlip
    maximum
    isQE
    lwop
    isAccrued
    superItems {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/PayItems_LeaveItemDetail').type;

export const LeaveList_LeaveQuery = gql`
  query leaveItems_list($bookId: ID!) {
    book(bookId: $bookId) {
      id
      leaveItems {
        id
        ...PayItems_LeaveItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/leaveItems_list').type;

export const PayItem_CreateLeaveItemMutation = gql`
  mutation createLeavePayItem($bookId: ID!, $input: CreateLeaveItemInput!) {
    createLeaveItem(bookId: $bookId, input: $input) {
      id
      ...PayItems_LeaveItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/createLeavePayItem').type;

export const PayItem_UpdateLeaveItemMutation = gql`
  mutation updateLeavePayItem(
    $bookId: ID!
    $id: ID!
    $input: UpdateLeaveItemInput!
  ) {
    updateLeaveItem(bookId: $bookId, id: $id, input: $input) {
      id
      ...PayItems_LeaveItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/updateLeavePayItem').type;

export const PayItem_DeleteLeaveItemMutation = gql`
  mutation deleteLeavePayItem($bookId: ID!, $id: ID!) {
    deleteLeaveItem(bookId: $bookId, id: $id) {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/deleteLeavePayItem').type;

// Allowance queries
export const allowanceItemDetailFragment = gql`
  fragment PayItems_AllowanceItemDetail on AllowanceItemDetail {
    id
    displayName
    status
    type
    defaultRate
    calculationBasis
    expenseAccount {
      id
      accountName
    }
    taxApplicableName
    description
    limitAmount
    limitType
    showAsAllowancePS
    typeName
    superItems {
      id
    }
    stp2Type
    subType
    isQE
  }
` as import('../../../../__generated__/ts-gql/PayItems_AllowanceItemDetail').type;

export const AllowanceList_AllowanceQuery = gql`
  query allowanceItems_listQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      allowanceItems {
        id
        ...PayItems_AllowanceItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/allowanceItems_listQuery').type;

export const PayItem_CreateAllowanceItemMutation = gql`
  mutation createAllowancePayItem($bookId: ID!, $input: CreateAllowanceInput!) {
    createAllowanceItem(bookId: $bookId, input: $input) {
      id
      ...PayItems_AllowanceItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/createAllowancePayItem').type;

export const PayItem_DeleteAllowanceItemMutation = gql`
  mutation deleteAllowancePayItem($bookId: ID!, $id: ID!) {
    deleteAllowanceItem(bookId: $bookId, id: $id) {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/deleteAllowancePayItem').type;

export const PayItem_UpdateAllowanceItemMutation = gql`
  mutation updateAllowancePayItem(
    $bookId: ID!
    $id: ID!
    $input: UpdateAllowanceInput!
  ) {
    updateAllowanceItem(bookId: $bookId, id: $id, input: $input) {
      id
      ...PayItems_AllowanceItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/updateAllowancePayItem').type;

// Deduction queries
export const deductionItemDetailFragment = gql`
  fragment PayItems_DeductionItemDetail on DeductionItemDetail {
    id
    displayName
    description
    status
    type
    defaultRate
    calculationBasis
    limitAmount
    limitType
    isDeductedForTax
    isPercentValue
    liabilityAccount {
      id
      accountName
    }
    superItems {
      id
    }
    typeName
    supplier {
      id
      name
    }
  }
` as import('../../../../__generated__/ts-gql/PayItems_DeductionItemDetail').type;

export type PayItemDeductionItemDetail = FragmentData<
  typeof deductionItemDetailFragment
>;

export const DeductionList_listQuery = gql`
  query deductionList_listQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      deductionItems {
        id
        ...PayItems_DeductionItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/deductionList_listQuery').type;
export const DeductionItems_listQuery = gql`
  query deductionItems_listQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      deductionItems {
        id
        ...PayItems_DeductionItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/deductionItems_listQuery').type;

export const PayItemPayrunDeductionFragment = gql`
  fragment DeductionItem_payruns on Payrun {
    id
    employees {
      id
      deductions {
        id
        payItem {
          id
        }
      }
    }
  }
` as import('../../../../__generated__/ts-gql/DeductionItem_payruns').type;

export const PayItem_CreateDeductionItemMutation = gql`
  mutation createDeductionPayItem($bookId: ID!, $input: CreateDeductionInput!) {
    createDeductionItem(bookId: $bookId, input: $input) {
      id
      ...PayItems_DeductionItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/createDeductionPayItem').type;

export const PayItem_DeleteDeductionItemMutation = gql`
  mutation deleteDeductionPayItem($bookId: ID!, $id: ID!) {
    deleteDeductionItem(bookId: $bookId, id: $id) {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/deleteDeductionPayItem').type;

export const PayItem_UpdateDeductionItemMutation = gql`
  mutation updateDeductionPayItem(
    $bookId: ID!
    $id: ID!
    $input: UpdateDeductionInput!
  ) {
    updateDeductionItem(bookId: $bookId, id: $id, input: $input) {
      id
      ...PayItems_DeductionItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/updateDeductionPayItem').type;

// Superannuation pay items

export const GetSuperItemsQuery = gql`
  query PayItemGetSuperItemsQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      superItems {
        id
        ...PayItems_SuperItemDetail
      }
    }
  }
` as import('../../../../__generated__/ts-gql/PayItemGetSuperItemsQuery').type;

export const superItemDetailFragment = gql`
  fragment PayItems_SuperItemDetail on SuperItemDetail {
    id
    displayName
    defaultRate
    status
    type
    description
    expenseAccount {
      id
      accountName
    }
    linkedPayItems {
      id
      allowances {
        id
      }
      deductions {
        id
      }
      earnings {
        id
      }
      leaves {
        id
      }
    }
    isDeductGrossBeforeTax
    isPercentageOfEarning
    isStatutoryRate
    isStatutoryThreshold
    limit
    minimum
    thresholdAmount
    rateType
    type
  }
` as import('../../../../__generated__/ts-gql/PayItems_SuperItemDetail').type;

export type PayItemSuperItemDetail = FragmentData<
  typeof superItemDetailFragment
>;

export const PayItem_UpdateSuperannuationMutation = gql`
  mutation updateSuperItem($input: UpdateSuperItemInput!) {
    updateSuperItem(input: $input) {
      id
      ...PayItems_SuperItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/updateSuperItem').type;

export const PayItem_CreateSuperannuationMutation = gql`
  mutation createSuperItem($input: CreateSuperItemInput!) {
    createSuperItem(input: $input) {
      id
      ...PayItems_SuperItemDetail
    }
  }
` as import('../../../../__generated__/ts-gql/createSuperItem').type;

export const PayItem_DeleteSuperannuationItemMutation = gql`
  mutation deleteSuperItem($input: DeleteSuperItemInput!) {
    deleteSuperItem(input: $input) {
      id
    }
  }
` as import('../../../../__generated__/ts-gql/deleteSuperItem').type;

export const DeductionsSupplier_ListQuery = gql`
  query deductionSupplier_listQuery($bookId: ID!) {
    book(bookId: $bookId) {
      id
      allSuppliers {
        id
        name
        status
      }
    }
  }
` as import('../../../../__generated__/ts-gql/deductionSupplier_listQuery').type;

export const PayItems_PayrollSettings = gql`
  query payItems_PayrollSettings($bookId: ID!) {
    book(bookId: $bookId) {
      id
      settings {
        id
        payroll {
          id
          accounts {
            id
            expenseAccountForRounding {
              id
            }
            expenseAccount {
              id
            }
            liabilityAccount {
              id
            }
            expenseAccountForCompanySuperContributions {
              id
            }
            liabilityAccountForWithholdingAmounts {
              id
            }
          }
        }
      }
    }
  }
` as import('../../../../__generated__/ts-gql/payItems_PayrollSettings').type;
